import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "~/components/ui/drawer"
import { Button } from "~/components/ui/button"
import type { Packages, UserPayment, Videos } from "@prisma/client"
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group"
import { Label } from "~/components/ui/label"
import Typography from "~/components/typography"
import { Separator } from "~/components/ui/separator"
import pluralize from "pluralize"
import visa from "~/assets/purchases/visa.webp"
import mastercard from "~/assets/purchases/mastercard.webp"
import discover from "~/assets/purchases/discover.webp"
import { ExclamationTriangleIcon, ReloadIcon, TrashIcon } from "@radix-ui/react-icons"
import { useEffect, useState } from "react"
import { CreditCardForm } from "~/components/credit-card-form"
import { cn } from "~/utils/shadecn"
import { useFetcher } from "@remix-run/react"
import type { action as cardAction } from "~/routes/_app+/cards"
import type { action as purchaseAction } from "~/routes/_app+/purchases"
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert"
import type { PurchaseWithPackage } from "~/purchases/purchases.server"
import { Input } from "~/components/ui/input"
import { Textarea } from "~/components/ui/textarea"
import gtag from "~/utils/trackers/gtag"
import { useCookies } from "react-cookie"
import { dotCart } from "~/utils/trackers/dot"
import showdown from "showdown"

export const CARDS = {
  Visa: visa,
  MasterCard: mastercard,
  Discover: discover,
}

export const expiry = (expDate: string) => {
  if (!expDate) return ""
  const parts = expDate.split("-")
  return `${parts[1]}/${parts[0].slice(-2)}`
}

export function Purchase({
  description,
  cards,
  pkg,
  additionalPkg,
  video,
  loveNoteVideoID,
  loveNoteComment,
  isAnonymous,
  starID,
  source,
  sourceID,
  open,
  setOpen,
  setRented,
  successMsg,
  gifting,
  setPurchase,
}: {
  description?: string | React.ReactNode
  cards: UserPayment[]
  pkg: Packages | null
  additionalPkg?: Packages
  video?: Videos
  loveNoteVideoID?: number
  loveNoteComment?: string
  isAnonymous?: boolean
  starID?: number
  source?: string
  sourceID?: number
  open: boolean
  setOpen: (open: boolean) => void
  setRented?: (open: boolean) => void
  gifting?: boolean
  setPurchase?: (p: PurchaseWithPackage) => void
  successMsg?: any
}) {
  const [cardOpen, setCardOpen] = useState(false)
  const fetcher = useFetcher<typeof purchaseAction>()
  const [alert, setAlert] = useState("")
  const [alertType, setAlertType] = useState<"warning" | "error" | undefined>()
  const [alertDesc, setAlertDesc] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [selectedCard, setSelectedCard] = useState("")
  const [successOpen, setSuccessOpen] = useState(false)
  const [assignableContact, setAssignableContact] = useState("")
  const [assignableNote, setAssignableNote] = useState("")
  const [fieldErrors, setFieldErrors] = useState({})
  const [cookies, setCookie, removeCookie] = useCookies<string>(["cart"])
  const converter = new showdown.Converter()

  useEffect(() => {
    setSubmitting(fetcher.state == "submitting")
  }, [fetcher.state])

  useEffect(() => {
    if (cards.length > 0) {
      setSelectedCard(cards.filter((card) => !card.DeclineCode)[0]?.UserPaymentID.toString())
    }
  }, [JSON.stringify(cards)])

  useEffect(() => {
    if (fetcher.data?.error) {
      setAlert(fetcher.data.error.alert)
      setAlertType(fetcher.data.error.class)
      setAlertDesc(fetcher.data.error.description)
    } else if (fetcher.data?.fieldErrors) {
      setFieldErrors(fetcher.data.fieldErrors)
    } else {
      setAlert("")
      setAlertDesc("")
      setFieldErrors({})
    }

    if (fetcher.data?.purchase) {
      if (setPurchase) setPurchase(fetcher.data.purchase)
      if (setRented) setRented(true)
      setTimeout(() => setSuccessOpen(true), 200)
      setOpen(false)
      gtag.event({
        action: "purchase",
        label: pkg!.Tag!,
        value: pkg!.Price!.toString(),
      })
      dotCart({ cookies, setCookie, removeCookie, pkg: pkg!, video, complete: true })
    }
  }, [fetcher.data])

  const submit = async () => {
    const formData = new FormData()
    formData.append("PackageTag", pkg!.Tag!)
    if (additionalPkg) formData.append("AdditionalPackageTag", additionalPkg.Tag!)
    formData.append("UserPaymentID", selectedCard)

    if (gifting) {
      formData.append("AssignableContact", assignableContact)
      formData.append("AssignableNote", assignableNote)
    }

    if (video) formData.append("VideoID", video.VideoID.toString())
    if (starID) formData.append("StarID", starID?.toString())
    if (loveNoteVideoID) formData.append("LoveNoteVideoID", loveNoteVideoID?.toString())
    if (loveNoteComment) formData.append("LoveNoteComment", loveNoteComment)
    if (isAnonymous) formData.append("IsAnonymous", "true")
    if (source) {
      formData.append("Source", source)
      formData.append("SourceID", sourceID!.toString())
    }
    fetcher.submit(formData, { action: "/purchases", method: "post" })
  }

  useEffect(() => {
    if (!open || !pkg) return
    dotCart({ cookies, setCookie, removeCookie, pkg, additionalPkg, video })
  }, [open])

  useEffect(() => {
    if (!open || !pkg) return
    console.log("pkg", pkg, "additionalPkg", additionalPkg)
  }, [open, pkg, additionalPkg])

  return (
    <>
      {pkg && (
        <>
          <Drawer open={open} onOpenChange={setOpen}>
            <DrawerContent className="z-30">
              <fetcher.Form className="mx-auto w-full max-w-lg" action="/purchases" method="post">
                <DrawerHeader>
                  <DrawerTitle>
                    {gifting && "Gift "}
                    {pkg.Name}
                    {pkg.RecurInterval && " Monthly Subscription"}
                  </DrawerTitle>
                  <DrawerDescription>
                    {description ?
                      <>
                        {typeof description === "string" ?
                          <span
                            dangerouslySetInnerHTML={{
                              __html: converter.makeHtml(description.replace(/(?:\r\n|\r|\n)/g, "  \r\n") || ""),
                            }}></span>
                        : <span>{description}</span>}
                      </>
                    : <span>
                        Receive <b className="text-foreground">{pkg.CreditsToAward}</b> #RealWorldSex video rentals{" "}
                        {pkg.RecurInterval && <>each month</>}
                      </span>
                    }
                  </DrawerDescription>
                  <Separator className="shrink-0 bg-border h-[1px] w-full my-2" />
                  {additionalPkg && (
                    <>
                      <DrawerDescription className="flex justify-between items-center">
                        <span>{pkg.Name}</span>
                        <span>${Number(pkg.Price)}</span>
                      </DrawerDescription>
                      <DrawerDescription className="flex justify-between items-center">
                        <span>{`${additionalPkg.Name} (Add-on)`}</span>
                        <span>${Number(additionalPkg.Price)}</span>
                      </DrawerDescription>
                      <Separator className="shrink-0 bg-border h-[1px] w-full my-2" />
                    </>
                  )}
                  <Typography>
                    {pkg.CreditsToAward === 0 ? null : (
                      <p className="text-sm text-muted-foreground items-center">
                        {pkg.CreditExpiryUnit >= 1000 ?
                          "Credits will never expire"
                        : <>
                            Unused rental credits will expire{" "}
                            {pkg.RecurInterval ?
                              "at the end of each billing cycle."
                            : `${pkg.CreditExpiryUnit} ${pluralize(pkg.CreditExpiryInterval, pkg.CreditExpiryUnit!)} after purchase.`
                            }
                          </>
                        }
                      </p>
                    )}
                    <h4 className="font-bold flex justify-between m-0">
                      <span>Total:</span>
                      <span>${(Number(pkg.Price) + Number(additionalPkg ? additionalPkg.Price : 0)).toString()}</span>
                    </h4>
                    <p className="text-muted-foreground text-right text-xs">
                      {pkg.RecurInterval ?
                        <>
                          Recurring every {pkg.RecurUnit} {pluralize(pkg.RecurInterval, pkg.RecurUnit!)}
                        </>
                      : "One time payment"}
                    </p>
                  </Typography>
                  {gifting && (
                    <div className="space-y-2 mt-8 mb-2">
                      <div className="space-y-2">
                        <Label className="mx-1" htmlFor="AssignableContact">
                          Recipients Email Address
                        </Label>
                        <div className="relative">
                          <Input
                            type="text"
                            id="AssignableContact"
                            name="AssignableContact"
                            value={assignableContact}
                            onChange={(e) => setAssignableContact(e.target.value)}
                            className="mt-4"
                          />
                        </div>
                        {fieldErrors["AssignableContact"] && (
                          <span className="text-[0.8rem] font-medium text-destructive">
                            {fieldErrors["AssignableContact"]}
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="mx-1" htmlFor="AssignableNote">
                          Personal Note <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <div className="relative">
                          <Textarea
                            id="AssignableNote"
                            name="AssignableNote"
                            value={assignableNote}
                            onChange={(e) => setAssignableNote(e.target.value)}
                          />
                        </div>
                        {fieldErrors["AssignableNote"] && (
                          <span className="text-[0.8rem] font-medium text-destructive">
                            {fieldErrors["AssignableNote"]}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <Typography>
                    <h4 className="mt-8">Choose your payment method:</h4>
                  </Typography>
                  <Separator className="my-2" />
                  {alert && (
                    <Alert variant={alertType == "warning" ? "warning" : "destructive"}>
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      <AlertTitle>{alert}</AlertTitle>
                      <AlertDescription>{alertDesc}</AlertDescription>
                    </Alert>
                  )}
                  <RadioGroup value={selectedCard} onValueChange={setSelectedCard} className="flex flex-col gap-2">
                    {cards.map((card, index) => (
                      <CardSelection key={index} card={card} />
                    ))}
                  </RadioGroup>
                  <Typography className="text-sm">
                    <p>
                      <Button
                        variant="link"
                        onClick={(e) => {
                          setCardOpen(true)
                          e.preventDefault()
                        }}>
                        + Add new card
                      </Button>
                    </p>
                  </Typography>
                </DrawerHeader>
                <DrawerFooter>
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <Button
                      disabled={submitting}
                      onClick={(e) => {
                        e.preventDefault()
                        submit()
                      }}>
                      {submitting ?
                        <span>
                          <ReloadIcon className="inline mr-2 h-4 w-4 animate-spin" />
                          {pkg.RecurUnit ? "Subscribing" : "Purchasing"}...
                        </span>
                      : <span>{pkg.RecurUnit ? "Subscribe" : "Purchase"}</span>}
                    </Button>
                    <DrawerClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DrawerClose>
                  </div>
                </DrawerFooter>
              </fetcher.Form>
              <CreditCardForm open={cardOpen} setOpen={setCardOpen} />
            </DrawerContent>
          </Drawer>

          <Drawer open={successOpen} onOpenChange={setSuccessOpen}>
            <DrawerContent className="z-30">
              <div className="mx-auto w-full max-w-lg">
                <DrawerHeader>
                  <Typography>
                    <h2 className="my-4 text-center text-success-foreground">Purchase Successful!</h2>
                    {pkg.RecurInterval && (
                      <>
                        <Separator className="shrink-0 bg-border h-[1px] w-full my-2" />
                        <p>Congratulations on your subscription!</p>

                        <p>
                          You now have access to thousands of full intro videos and{" "}
                          <b className="text-foreground">{pkg.CreditsToAward}</b> credits per month to rent any{" "}
                          <b className="text-foreground">{pkg.CreditsToAward}</b> real world sex videos in full. One
                          rental = 4 weeks of unlimited viewing. Happy exploring! You have great taste ;)
                        </p>
                        <Separator className="shrink-0 bg-border h-[1px] w-full my-2" />
                      </>
                    )}
                  </Typography>

                  <DrawerTitle>
                    {gifting && "Gift "}
                    {pkg.Name}
                    {pkg.RecurInterval && " Monthly Subscription"}
                  </DrawerTitle>
                  <DrawerDescription>
                    {description ?
                      <span>{description}</span>
                    : <span>
                        Receive <b className="text-foreground">{pkg.CreditsToAward}</b> #RealWorldSex video rentals{" "}
                        {pkg.RecurInterval && <>each month</>}
                      </span>
                    }
                  </DrawerDescription>
                  <Separator className="shrink-0 bg-border h-[1px] w-full my-2" />
                  {additionalPkg && (
                    <>
                      <DrawerDescription className="flex justify-between items-center">
                        <span>{pkg.Name}</span>
                        <span>${Number(pkg.Price)}</span>
                      </DrawerDescription>
                      <DrawerDescription className="flex justify-between items-center">
                        <span>{`${additionalPkg.Name} (Add-on)`}</span>
                        <span>${Number(additionalPkg.Price)}</span>
                      </DrawerDescription>
                      <Separator className="shrink-0 bg-border h-[1px] w-full my-2" />
                    </>
                  )}
                  <Typography>
                    {pkg.CreditsToAward === 0 ? null : (
                      <p className="text-sm text-muted-foreground items-center">
                        {pkg.CreditExpiryUnit >= 1000 ?
                          "Credits will never expire"
                        : <>
                            Unused rental credits will expire{" "}
                            {pkg.RecurInterval ?
                              "at the end of each billing cycle."
                            : `${pkg.CreditExpiryUnit} ${pluralize(pkg.CreditExpiryInterval, pkg.CreditExpiryUnit!)} after purchase.`
                            }
                          </>
                        }
                      </p>
                    )}
                    <h4 className="font-bold flex justify-between m-0">
                      <span>Total:</span>
                      <span>${(Number(pkg.Price) + Number(additionalPkg ? additionalPkg.Price : 0)).toString()}</span>
                    </h4>
                    <p className="text-muted-foreground text-right text-xs">
                      {pkg.RecurInterval ?
                        <>
                          Recurring every {pkg.RecurUnit} {pluralize(pkg.RecurInterval, pkg.RecurUnit!)}
                        </>
                      : "One time payment"}
                    </p>
                  </Typography>
                  {successMsg && <div className="mt-10">{successMsg}</div>}
                </DrawerHeader>
                <DrawerFooter className="flex justify-center mt-6 mb-8">
                  <DrawerClose asChild>
                    <Button variant="outline">Close</Button>
                  </DrawerClose>
                </DrawerFooter>
              </div>
            </DrawerContent>
          </Drawer>
        </>
      )}
    </>
  )
}

export function ChangeCreditCard({
  cards,
  purchase,
  open,
  setOpen,
}: {
  cards: UserPayment[]
  purchase: PurchaseWithPackage
  open: boolean
  setOpen: (open: boolean) => void
}) {
  const [cardOpen, setCardOpen] = useState(false)
  const fetcher = useFetcher()
  const [alert, setAlert] = useState("")
  const [alertType, setAlertType] = useState<"warning" | "error" | undefined>()
  const [alertDesc, setAlertDesc] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [selectedCard, setSelectedCard] = useState("")
  const [success, setSuccess] = useState("")
  const [fieldErrors, setFieldErrors] = useState({})

  useEffect(() => {
    setSubmitting(fetcher.state == "submitting")
  }, [fetcher.state])

  useEffect(() => {
    if (cards.length > 0) {
      setSelectedCard(
        cards.filter((card) => card.UserPaymentID === purchase.UserPaymentID)[0]?.UserPaymentID.toString()
      )
    }
  }, [JSON.stringify(cards)])

  useEffect(() => {
    if (fetcher.data?.error) {
      setAlert(fetcher.data.error.alert)
      setAlertType(fetcher.data.error.class)
      setAlertDesc(fetcher.data.error.description)
    } else if (fetcher.data?.fieldErrors) {
      setFieldErrors(fetcher.data.fieldErrors)
    } else {
      setAlert("")
      setAlertDesc("")
      setFieldErrors({})
    }

    if (fetcher.data?.success) {
      setSuccess("Successfully updated the credit card for this subscription.")
    }
  }, [fetcher.data])

  const submit = async () => {
    const formData = new FormData()
    formData.append("PurchaseID", purchase.PurchaseID.toString())
    formData.append("type", "updateCard")
    formData.append("UserPaymentID", selectedCard)
    fetcher.submit(formData, { action: "/purchases", method: "put" })
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent className="z-30">
        <fetcher.Form className="mx-auto w-full max-w-lg" action="/purchases" method="put">
          <DrawerHeader>
            {success ?
              <DrawerTitle className="text-success-foreground">{success}</DrawerTitle>
            : <DrawerTitle>Update Credit Card</DrawerTitle>}
            <Separator className="my-2" />
            <Typography>
              <h4 className="mt-8">Choose your payment method:</h4>
            </Typography>
            <Separator className="my-2" />
            {alert && (
              <Alert variant={alertType == "warning" ? "warning" : "destructive"}>
                <ExclamationTriangleIcon className="h-4 w-4" />
                <AlertTitle>{alert}</AlertTitle>
                <AlertDescription>{alertDesc}</AlertDescription>
              </Alert>
            )}
            <RadioGroup value={selectedCard} onValueChange={setSelectedCard} className="flex flex-col gap-2">
              {cards.map((card, index) => (
                <CardSelection key={index} card={card} />
              ))}
            </RadioGroup>
            <Typography className="text-sm">
              <p>
                <Button
                  variant="link"
                  onClick={(e) => {
                    setCardOpen(true)
                    e.preventDefault()
                  }}>
                  + Add new card
                </Button>
              </p>
            </Typography>
          </DrawerHeader>
          <DrawerFooter>
            <div className="grid grid-cols-2 gap-4 mt-6">
              <Button
                disabled={submitting}
                onClick={(e) => {
                  e.preventDefault()
                  submit()
                }}>
                {submitting ?
                  <span>
                    <ReloadIcon className="inline mr-2 h-4 w-4 animate-spin" />
                    Updating Card...
                  </span>
                : <span>Update Card</span>}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline">Cancel</Button>
              </DrawerClose>
            </div>
          </DrawerFooter>
        </fetcher.Form>
        <CreditCardForm open={cardOpen} setOpen={setCardOpen} />
      </DrawerContent>
    </Drawer>
  )
}

export function ChangeSubscription({
  cards,
  purchase,
  pkg,
  open,
  setOpen,
  successMsg,
  setPurchase,
}: {
  cards: UserPayment[]
  pkg: Packages | null
  purchase: PurchaseWithPackage
  open: boolean
  setOpen: (open: boolean) => void
  setPurchase?: (p: PurchaseWithPackage) => void
  successMsg?: any
}) {
  const [cardOpen, setCardOpen] = useState(false)
  const fetcher = useFetcher<typeof purchaseAction>()
  const [alert, setAlert] = useState("")
  const [alertType, setAlertType] = useState<"warning" | "error" | undefined>()
  const [alertDesc, setAlertDesc] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [selectedCard, setSelectedCard] = useState("")
  const [successOpen, setSuccessOpen] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [deltaAmount, setDeltaAmount] = useState(0)

  useEffect(() => {
    setSubmitting(fetcher.state == "submitting")
  }, [fetcher.state])

  useEffect(() => {
    if (!pkg || !purchase) return

    setDeltaAmount(0)
    if (pkg.Price > purchase.Package.Price) {
      setDeltaAmount(Number(pkg.Price) - Number(purchase.Package.Price))
    }
  }, [pkg, purchase])

  useEffect(() => {
    if (cards.length > 0) {
      setSelectedCard(
        cards.filter((card) => card.UserPaymentID === purchase.UserPaymentID)[0]?.UserPaymentID.toString()
      )
    }
  }, [JSON.stringify(cards)])

  useEffect(() => {
    if (fetcher.data?.error) {
      setAlert(fetcher.data.error.alert)
      setAlertType(fetcher.data.error.class)
      setAlertDesc(fetcher.data.error.description)
    } else if (fetcher.data?.fieldErrors) {
      setFieldErrors(fetcher.data.fieldErrors)
    } else {
      setAlert("")
      setAlertDesc("")
      setFieldErrors({})
    }

    if (fetcher.data?.purchase) {
      if (setPurchase) setPurchase(fetcher.data.purchase)
      setTimeout(() => setSuccessOpen(true), 200)
      setOpen(false)
    }
  }, [fetcher.data])

  const submit = async () => {
    const formData = new FormData()
    formData.append("PackageTag", pkg!.Tag!)
    formData.append("PurchaseID", purchase!.PurchaseID!.toString())
    formData.append("UserPaymentID", selectedCard)
    formData.append("type", "updatePkg")
    fetcher.submit(formData, { action: "/purchases", method: "put" })
  }

  return (
    <>
      {purchase && pkg && pkg.RecurInterval && pkg.RecurUnit && (
        <>
          <Drawer open={open} onOpenChange={setOpen}>
            <DrawerContent className="z-30">
              <fetcher.Form className="mx-auto w-full max-w-lg" action="/purchases" method="put">
                <DrawerHeader>
                  <DrawerTitle>
                    Updating Subscription from {purchase.Package.Name} to {pkg.Name}
                  </DrawerTitle>
                  <DrawerDescription className="text-pretty">
                    Subscription renews at <b>${Number(pkg.Price)}</b> and you'll receive <b>{pkg.CreditsToAward}</b>{" "}
                    credits per month.
                  </DrawerDescription>
                  <Separator className="my-2" />
                  {deltaAmount > 0 && (
                    <div className="grid gap-2">
                      <div className="flex justify-between text-sm text-muted-foreground items-center">
                        <p>
                          <b>Upgrade plan:</b> {purchase.Package.Name} ($
                          {Number(purchase.Package.Price)}) -&gt; {pkg.Name} ($
                          {Number(pkg.Price)})
                        </p>
                        <p>
                          <b>${deltaAmount}</b>
                        </p>
                      </div>
                      {pkg.CreditsToAward - purchase.Package.CreditsToAward > 0 && (
                        <div className="flex justify-between text-sm text-muted-foreground items-center">
                          <p>
                            <b>Additional credits:</b> {purchase.Package.Name} ({purchase.Package.CreditsToAward}) -&gt;{" "}
                            {pkg.Name} ({pkg.CreditsToAward})
                          </p>
                          <p>
                            <b>{pkg.CreditsToAward - purchase.Package.CreditsToAward} credits</b>
                          </p>
                        </div>
                      )}
                      <Separator className="my-2" />
                    </div>
                  )}
                  <Typography>
                    <h4 className="font-bold flex justify-between m-0">
                      <span>Due Today:</span>
                      <span>${deltaAmount}</span>
                    </h4>
                    <p className="text-muted-foreground text-right text-xs">
                      ${Number(pkg.Price)} recurring every{" "}
                      {pkg.RecurUnit === 1 ?
                        pluralize(pkg.RecurInterval, 1)
                      : `${pkg.RecurUnit} ${pluralize(pkg.RecurInterval, pkg.RecurUnit)}`}
                    </p>
                  </Typography>
                  {deltaAmount > 0 && (
                    <>
                      <Typography>
                        <h4 className="mt-8">Choose your payment method:</h4>
                      </Typography>
                      <Separator className="my-2" />
                      {alert && (
                        <Alert variant={alertType == "warning" ? "warning" : "destructive"}>
                          <ExclamationTriangleIcon className="h-4 w-4" />
                          <AlertTitle>{alert}</AlertTitle>
                          <AlertDescription>{alertDesc}</AlertDescription>
                        </Alert>
                      )}
                      <RadioGroup value={selectedCard} onValueChange={setSelectedCard} className="flex flex-col gap-2">
                        {cards.map((card, index) => (
                          <CardSelection key={index} card={card} />
                        ))}
                      </RadioGroup>
                      <Typography className="text-sm">
                        <p>
                          <Button
                            variant="link"
                            onClick={(e) => {
                              setCardOpen(true)
                              e.preventDefault()
                            }}>
                            + Add new card
                          </Button>
                        </p>
                      </Typography>
                    </>
                  )}
                </DrawerHeader>
                <DrawerFooter>
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <Button
                      disabled={submitting}
                      onClick={(e) => {
                        e.preventDefault()
                        submit()
                      }}>
                      {submitting ?
                        <span>
                          <ReloadIcon className="inline mr-2 h-4 w-4 animate-spin" /> Changing Subscription...
                        </span>
                      : <span>Change Subscription</span>}
                    </Button>
                    <DrawerClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DrawerClose>
                  </div>
                </DrawerFooter>
              </fetcher.Form>
              <CreditCardForm open={cardOpen} setOpen={setCardOpen} />
            </DrawerContent>
          </Drawer>

          <Drawer open={successOpen} onOpenChange={setSuccessOpen}>
            <DrawerContent className="z-30">
              <div className="mx-auto w-full max-w-lg">
                <DrawerHeader>
                  <Typography>
                    <h2 className="my-4 text-center text-success-foreground">Subscription Changed!</h2>
                  </Typography>
                  <DrawerTitle>
                    {pkg.Name}
                    {pkg.RecurInterval && " Monthly Subscription"}
                  </DrawerTitle>
                  <DrawerDescription>
                    On subscription renewal, we'll charge <b>${Number(pkg.Price)}</b> and you'll receive{" "}
                    <b>{pkg.CreditsToAward}</b> credits per month.
                  </DrawerDescription>
                  <Separator className="my-2" />
                  <Typography>
                    <h4 className="font-bold flex justify-between m-0">
                      <span>Recurring Amount:</span>
                      <span>${pkg.Price.toString()}</span>
                    </h4>
                    <p className="text-muted-foreground text-right text-xs">
                      Recurring every{" "}
                      {pkg.RecurUnit === 1 ?
                        pluralize(pkg.RecurInterval, 1)
                      : `${pkg.RecurUnit} ${pluralize(pkg.RecurInterval, pkg.RecurUnit)}`}
                    </p>
                  </Typography>
                  {successMsg && <div className="mt-10">{successMsg}</div>}
                </DrawerHeader>
                <DrawerFooter className="flex justify-center mt-6 mb-8">
                  <DrawerClose asChild>
                    <Button variant="outline">Close</Button>
                  </DrawerClose>
                </DrawerFooter>
              </div>
            </DrawerContent>
          </Drawer>
        </>
      )}
    </>
  )
}

export function PaymentMethods({
  cards,
  open,
  setOpen,
}: {
  cards: UserPayment[]
  open: boolean
  setOpen: (open: boolean) => void
}) {
  const [cardOpen, setCardOpen] = useState(false)
  const [selectedCard, setSelectedCard] = useState(
    cards.filter((card) => !card.DeclineCode)[0]?.UserPaymentID.toString()
  )

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent className="z-30">
        <div className="mx-auto w-full max-w-lg">
          <DrawerHeader>
            <DrawerTitle>Update Payment Methods</DrawerTitle>
            <Separator className="my-2" />
            <RadioGroup value={selectedCard} onValueChange={setSelectedCard} className="flex flex-col gap-2">
              {cards.map((card, index) => (
                <CardSelection key={index} card={card} />
              ))}
            </RadioGroup>
            <Typography className="text-sm">
              <p>
                <Button
                  variant="link"
                  onClick={(e) => {
                    setCardOpen(true)
                    e.preventDefault()
                  }}>
                  + Add new card
                </Button>
              </p>
            </Typography>
          </DrawerHeader>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="outline">Close</Button>
            </DrawerClose>
          </DrawerFooter>
          <CreditCardForm open={cardOpen} setOpen={setCardOpen} />
        </div>
      </DrawerContent>
    </Drawer>
  )
}

function CardSelection({ card }: { card: UserPayment }) {
  const fetcher = useFetcher<typeof cardAction>()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setSubmitting(fetcher.state == "submitting")
  }, [fetcher.state])

  function submit(e) {
    const formData = new FormData()
    formData.append("UserPaymentID", card.UserPaymentID.toString())
    fetcher.submit(formData, { action: "/cards", method: "delete" })
    e.preventDefault()
  }

  return (
    <div className="flex justify-between hover:bg-accent">
      <Label
        className={cn(
          "flex items-center font-mono text-xs px-2",
          card.DeclineCode ? "text-muted-foreground" : "cursor-pointer"
        )}>
        <RadioGroupItem
          className={cn(card.DeclineCode && "text-muted-foreground border-muted-foreground")}
          value={card.UserPaymentID.toString()}
          disabled={!!card.DeclineCode}
        />
        <img className="w-8 mr-2 ml-4" src={CARDS[card.CardType]} />
        <span className="mr-2">ending in {card.CardNumber!.replace(/X/g, "")}</span>
        <span>exp: {expiry(card.CardExpDate!)}</span>
        {card.DeclineCode && (
          <>
            {card.DeclineCode == "4" ?
              <span className="ml-2">
                &nbsp;
                <i className="bg-warning text-warning-foreground rounded p-1">
                  {card.DeclineCode == "4" ?
                    "in review"
                  : card.DeclineCode == "2" ?
                    "declined"
                  : "error"}
                </i>
              </span>
            : <span className="text-destructive ml-2">
                &nbsp;
                <i>
                  {card.DeclineCode == "4" ?
                    "in review"
                  : card.DeclineCode == "2" ?
                    "declined"
                  : "error"}
                </i>
              </span>
            }
          </>
        )}
      </Label>

      <Button variant="link" className="text-xs" disabled={submitting} onClick={submit}>
        {submitting ?
          <span>
            <ReloadIcon className="inline mr-2 h-4 w-4 animate-spin" />
            deleting...
          </span>
        : <span>
            <TrashIcon className="inline" /> delete
          </span>
        }
      </Button>
    </div>
  )
}
